<template>
  <div class="full-page-width relative locations">
    <div class="row">
      <div class="col-xs-4 text-box">
        <InfoTextBox
          :text="$t('agreements-info-text')"
          :title="$t('Agreements')"
          :btnText="$t('Add agreement')"
          @action="addNewCarrier" />
      </div>
      <div class="col-xs-8">
        <ul class="list-table">
          <li
            @click="goToCarrierAccount(item.id)"
            class="list-table-item"
            v-for="(item, index) in get_carrier_accounts.items"
            :key="`item-${index}`"
          >
            <div class="table-cell small">
              <div class="status-logo-box float-left">
                <LogosSVG
                  :name="item.shipping_agent_name"
                  size="45" />
                <div class="status-indicator" :class="parseInt(item.is_active) === 1 ? 'active' : ''"></div>
              </div>
            </div>
            <div class="table-cell">
              <p>
                <span class="main-info">
                  {{ `${item.display_account_name}` }}
                </span>
                <span class="additional-info">
                  {{ `${item.integration_name}` }}
                </span>
              </p>
            </div>
          </li>
        </ul>
      </div>
    </div>
    <Popup
      v-if="openPopup"
      :popupSmall=true
      :isPopupSubmitDisabled="isPopupSubmitDisabled"
      @cancel="togglePopup"
      @confirm="submitForm">
      <template v-slot:header>
        <h2>{{ $t('Add agreement') }}</h2>
      </template>
      <div>
        <div>
          <InputSelect v-if="get_user.is_admin"
            class="m-b-20"
            :multiSelect=true
            :preselected="accessibleForSubaccounts"
            :label="$t('accessible_for_accounts')"
            @change="selectAccessibleSubaccount"
            :rules="{required: true}"
            :options="getAllowedSubaccounts" />
          <InputText
            class="m-b-20"
            :label="$t('account_name')"
            v-model="accountName"
            :rules="{required: true}"
          />
<!--          <InputSelect-->
<!--            class="m-b-20"-->
<!--            :label="$t('status')"-->
<!--            v-model="status"-->
<!--            :options="statusOptions"-->
<!--          />-->
        </div>
        <div>
          <InputSelect
            class="m-b-20"
            :label="$t('shipping_agent_name')"
            v-model="carrier"
            @change="callIntegrationForCarrierSelected"
            :options="get_shipping_agents_select"
            :rules="{required: true}"
          />
          <InputSelect
            v-if="showIntegration"
            class="m-b-20"
            :label="$t('integration')"
            @change="callIntegrationParams"
            v-model="integration"
            :options="get_integrations"
            :rules="{required: true}" />
          <div v-if="integration !== ''">
            <!-- LOGIN PARAMS -->
            <div v-for="(param, index) in loginParams" :key="`login-${index}`">
              <InputSelect
                v-if="param === 'CountryCode'"
                class="m-b-20"
                :label="$t('agreement_country')"
                v-model="loginParameters[param]"
                :options="[{value: 'DK', text: $t('Denmark')}, {value: 'SE', text: $t('Sweden')}, {value: 'FI', text: $t('Finland')}, {value: 'NO', text: $t('Norway')}]"
                :rules="{required: true}" />
              <InputText v-else class="m-b-20" :label="param" v-model="loginParameters[param]"
              />
            </div>
          </div>
          <div class="row" v-if="integration !== ''">
            <!-- BARCODE PARAMS -->
            <div
              class="col-xs-6"
              v-for="(param, index) in barcodeParams"
              :key="`barcode-${index}`"
            >
              <InputText
                class="m-b-20"
                :label="param"
                v-model="barcodeParameters[param]"
              />
            </div>
          </div>
          <!-- FedEx CSP Registration section for new account -->
          <div class="row m-t-20 m-b-20" v-if="showFedExSection">
            <div class="col-xs-12">
              <InfoTextBox
                :text="$t('fedex_csp_registration_info_text')"
                :title="$t('fedex_csp_registration')"
                :btnText="$t('register_fedex_account')"
                :btnIcon="'vpn_key'"
                @action="openFedExWizard" />
            </div>
            <div class="col-xs-12 m-t-10" v-if="loginParameters.ChildKey">
              <div class="card p-15" style="background: #f8fff9; border: 1px solid #c3e6cb;">
                <span class="text-success">✅ {{ $t('account_registered') }} (ChildKey: {{ loginParameters.ChildKey.substring(0, 8) }}...)</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Popup>

    <!-- FedEx CSP Wizard Popup -->
    <Popup
      v-if="fedexWizard.open"
      :popupSmall=true
      minHeight="500px"
      @cancel="fedexWizard.open = false"
      :showConfirm="false">
      <template v-slot:header>
        <h2>{{ $t('FedEx CSP Registration') }} - {{ $t('Step') }} {{ fedexWizard.step }}</h2>
      </template>
      <div v-if="fedexWizard.step === 1">
        <InputText class="m-b-20" :label="$t('Account Number') + ' *'" v-model="fedexWizard.data.accountNumber.value" :getErrorField="fedexWizard.errors['accountNumber.value']" />
        <InputText class="m-b-20" :label="$t('customer_name') + ' *'" v-model="fedexWizard.data.customerName" :getErrorField="fedexWizard.errors['customerName']" />
        <InputText class="m-b-10" :label="$t('Street') + ' *'" v-model="fedexWizard.data.address.streetLines[0]" :getErrorField="fedexWizard.errors['address.streetLines[0]']" />
        <div class="row m-b-10">
          <div class="col-xs-6"><InputText :label="$t('City') + ' *'" v-model="fedexWizard.data.address.city" :getErrorField="fedexWizard.errors['address.city']" /></div>
          <div class="col-xs-6"><InputText :label="$t('Postal Code') + ' *'" v-model="fedexWizard.data.address.postalCode" :getErrorField="fedexWizard.errors['address.postalCode']" /></div>
        </div>
        <div class="row m-b-20">
          <div class="col-xs-6"><InputText :label="$t('State/Province Code')" v-model="fedexWizard.data.address.stateOrProvinceCode" :getErrorField="fedexWizard.errors['address.stateOrProvinceCode']" /></div>
          <div class="col-xs-6">
            <InputSelect
              :label="$t('country') + ' *'"
              v-model="fedexWizard.data.address.countryCode"
              :options="countriesOptions"
              :rules="{required: true}"
              :getErrorField="fedexWizard.errors['address.countryCode']"
            />
          </div>
        </div>
        <div class="m-t-10 text-right">
          <Btn class="button blue" :btnText="$t('validate_address')" @btnClick="validateFedExAddress" :loading="fedexWizard.loading" />
        </div>
      </div>
      <div v-if="fedexWizard.step === 2">
        <InputText class="m-b-10" :label="$t('Invoice Number') + ' *'" v-model="fedexWizard.invoice.number" :getErrorField="fedexWizard.errors['invoice.number']" />
        <InputText class="m-b-10" :label="$t('Invoice Date (YYYY-MM-DD)') + ' *'" v-model="fedexWizard.invoice.date" :getErrorField="fedexWizard.errors['invoice.date']" />
        <CurrencySelect
          class="m-b-20"
          :label="$t('Invoice Amount') + ' *'"
          v-model="fedexInvoiceAmountCurrency"
          :getErrorField="fedexWizard.errors['invoice.amount'] || fedexWizard.errors['invoice.currency']"
        />
        <div class="text-right">
          <Btn :btnText="$t('back')" class="button gray m-r-10" @btnClick="fedexWizard.step = 1" />
          <Btn class="button blue" :btnText="$t('validate_invoice')" @btnClick="validateFedExInvoice" :loading="fedexWizard.loading" />
        </div>
      </div>
      <div v-if="fedexWizard.step === 3">
        <div class="text-center p-20">
          <Icon name="check_circle" size="64" fill-color="#28a745" />
          <h3>{{ $t('registration_complete') }}</h3>
          <p>{{ $t('fedex_account_registered_successfully') }}</p>
          <Btn class="button blue" :btnText="$t('finish')" @btnClick="fedexWizard.open = false" />
        </div>
      </div>
    </Popup>
  </div>
</template>
<script>
/* eslint-disable */
import { mapGetters } from 'vuex';
import axios from 'axios';
import types from '@/store/types';
import Btn from '@/components/elements/buttons';
import InputText from '@/components/elements/inputs/text.vue';
import CurrencySelect from '@/components/elements/inputs/currencySelect.vue';
import Popup from '@/components/global/popup';
import InputSelect from '@/components/elements/inputs/select.vue';
import Toggle from '@/components/elements/inputs/toggle.vue';
import LogosSVG from "@/assets/images/logosSVG";

import InfoTextBox from '@/components/global/infoTextBox';
import formValidator from '@/mixins/formValidator';

export default {
  name: 'carrierAccounts',
  mixins: [formValidator],
  components: {
    Btn,
    Popup,
    InputText,
    InputSelect,
    CurrencySelect,
    Toggle,
    LogosSVG,
    InfoTextBox,
  },
  data() {
    return {
      openPopup: false,
      isEdit: false,
      // POPUP DATA
      accessibleForSubaccounts: [],
      accountName: '',
      status: 1,
      statusOptions: [
        { text: 'Active', value: 1 },
        { text: 'Inactive', value: 0 },
      ],
      carrier: '',
      showIntegration: false,
      integration: '',
      loginParameters: {},
      barcodeParameters: {},
      fedexWizard: {
        open: false,
        loading: false,
        step: 1,
        accountAuthToken: null,
        authOptions: [],
        errors: {},
        countries: [],
        data: {
          accountNumber: { value: '' },
          address: {
            streetLines: [''],
            city: '',
            stateOrProvinceCode: '',
            postalCode: '',
            countryCode: '',
            residential: true
          },
          customerName: ''
        },
        invoice: {
          number: '',
          date: '',
          amount: '',
          currency: 'USD'
        }
      },
    };
  },
  computed: {
    ...mapGetters([
      'get_carrier_accounts',
      'get_carrier_account_by_id',
      'get_shipping_agents',
      'get_shipping_agents_select',
      'get_integrations',
      'get_integration_params',
      'get_integration_params_barcode',
      'get_user',
      'get_subaccounts'
    ]),
    isPopupSubmitDisabled() {
      if(this.get_user.is_admin && this.accessibleForSubaccounts.length == 0) return true;
      return this.validateInput([
        {
          formFeild:'accountName',
          value: this.accountName,
          validators:{required: true}
        },
        {
          formFeild:'carrier',
          value: this.carrier,
          validators:{required: true}
        },
        {
          formFeild:'integration',
          value: this.integration,
          validators:{required: true}
        },
      ]);
    },
    getAllowedSubaccounts() {
      return this.get_subaccounts.filter((acc) => this.get_user.accessible_subaccounts_array.indexOf(String(acc.id)) > -1).map((acc) => ({text: acc.name, value: String(acc.id),}));
    },
    loginParams() {
      return this.get_integration_params.login;
    },
    barcodeParams() {
      return this.get_integration_params_barcode;
    },
    isFedExCsp() {
      return parseInt(this.integration) === 26;
    },
    isBackoffice1() {
      return parseInt(this.get_user.backoffice_id) === 1;
    },
    showFedExSection() {
      return this.isFedExCsp && this.isBackoffice1;
    },
    fedexInvoiceAmountCurrency: {
      get() {
        return {
          value: this.fedexWizard.invoice.amount,
          unit: (this.fedexWizard.invoice.currency || 'USD').toLowerCase()
        };
      },
      set(val) {
        this.fedexWizard.invoice.amount = val.value;
        this.fedexWizard.invoice.currency = val.unit.toUpperCase();
      }
    },
    countriesOptions() {
      return this.fedexWizard.countries.map(c => ({
        value: c.alpha2,
        text: c.name
      }));
    }
  },
  created() {
    this.$store.dispatch('GET_CARRIER_ACCOUNTS');
    this.fetchCountries();
  },
  methods: {
    selectAccessibleSubaccount(val) {
        this.accessibleForSubaccounts = val;
    },
    resetBarcodeAndLoginData() {
      this.loginParameters = new Object();
      this.barcodeParameters = new Object();
    },
    resetFormData() {
      this.accessibleForSubaccounts = [];
      this.accountName = '';
      this.status = 1;
      this.carrier = '';
      this.showIntegration = false;
      this.integration = '';
      this.resetBarcodeAndLoginData();
      this.fedexWizard.step = 1;
      this.fedexWizard.data = {
        accountNumber: { value: '' },
        address: {
          streetLines: [''],
          city: '',
          stateOrProvinceCode: '',
          postalCode: '',
          countryCode: '',
          residential: true
        },
        customerName: ''
      };
      this.fedexWizard.invoice = {
        number: '',
        date: '',
        amount: '',
        currency: 'USD'
      };
      this.fedexWizard.errors = {};
    },
    togglePopup() {
      this.openPopup = !this.openPopup;
      this.accountId = '';
      this.resetFormData();
    },
    submitForm() {
      this.$store.dispatch('SET_CARRIER_ACCOUNT', {
        accessible_for_subaccounts: this.accessibleForSubaccounts,
        is_active: 0, //this.status,
        account_name: this.accountName,
        integration_id: this.integration,
        company_id: this.get_user.company_id,
        integration_type: 2, // ovo je za admin carrier account - pitaj Igora
        shipping_agent_id: this.carrier,
        have_proactive_support: 0,
        should_create_sales_orders: 0,
        barcode_json: {
          0: {
            integration_id: this.integration,
            params: this.barcodeParameters,
          },
        },
        login_parameters_json: {
          0: {
            integration_id: this.integration,
            params: this.loginParameters,
          },
        },
      }).then((res) => {
        this.togglePopup();
        this.$store.dispatch('GET_CARRIER_ACCOUNTS');
      });
    },
    goToCarrierAccount(id) {
      this.$router.push({ path: `agreements/${id}` });
      // this.$router.push({ name:'agreements', id: id });
    },
    addNewCarrier() {
      this.isEdit = false;
      this.togglePopup();
      this.$store.dispatch('GET_SHIPPING_AGENTS');
      this.integration = '';
      this.resetFormData();
    },
    fetchCountries() {
      axios.get('/popup-data/get-countries').then(res => {
        this.fedexWizard.countries = res.data.data || [];
      }).catch(err => {
        console.error('Error fetching countries:', err);
      });
    },
    callIntegrationForCarrierSelected(val) {
      this.showIntegration = false;
      this.integration = '';
      this.resetBarcodeAndLoginData();
      this.$store.dispatch('CLEAR_INTEGRATION_PARAMS');
      this.$store.dispatch('GET_INTEGRATION_BY_CARRIER', val).then(() => {
        this.showIntegration = true;
      });
    },
    callIntegrationParams(val) {
      this.integration = val;
      this.$store.dispatch('CLEAR_INTEGRATION_PARAMS');
      this.$store
        .dispatch('GET_INTEGRATION_PARAMS', {
          agentID: this.carrier,
          integrationID: val,
        })
        .then(() => {
          this.resetBarcodeAndLoginData();
          // LOGIN
          this.get_integration_params.login.forEach((element) => {
            this.$set(this.loginParameters, element, '');
          });

          // BARCODE
          this.get_integration_params_barcode.forEach((element) => {
            this.$set(this.barcodeParameters, element, '');
          });
        });
    },
    openEditPopup() {
      this.togglePopup();
      this.isEdit = true;
    },
    openFedExWizard() {
      this.fedexWizard.step = 1;
      this.fedexWizard.open = true;
      this.fedexWizard.errors = {};
      this.fedexWizard.data.accountNumber.value = this.loginParameters.ShippingAccount || '';
    },
    async validateFedExAddress() {
      this.fedexWizard.errors = {};
      const requiredFields = [
        { key: 'accountNumber.value', label: 'Account Number' },
        { key: 'customerName', label: 'Customer Name' },
        { key: 'address.streetLines[0]', label: 'Street' },
        { key: 'address.city', label: 'City' },
        { key: 'address.postalCode', label: 'Postal Code' },
        { key: 'address.countryCode', label: 'Country' }
      ];

      let hasErrors = false;
      requiredFields.forEach(field => {
        const value = field.key.split('.').reduce((o, i) => {
          if (i.includes('[')) {
            const [name, index] = i.split(/[\[\]]/);
            return o[name][index];
          }
          return o[i];
        }, this.fedexWizard.data);

        if (!value || (typeof value === 'string' && value.trim() === '')) {
          this.$set(this.fedexWizard.errors, field.key, 'Field is required');
          hasErrors = true;
        }
      });

      if (hasErrors) {
        this.$store.commit(types.SHOW_NOTIFICATION_BAR, { type: 'error', message: 'Please fill in all required fields' });
        return;
      }

      const data = JSON.parse(JSON.stringify(this.fedexWizard.data));
      data.accountNumber.value = data.accountNumber.value.trim();
      data.customerName = data.customerName.trim();
      data.address.streetLines[0] = data.address.streetLines[0].trim();
      data.address.city = data.address.city.trim();
      data.address.postalCode = data.address.postalCode.trim();
      data.address.countryCode = data.address.countryCode.trim();
      if (data.address.stateOrProvinceCode) {
        data.address.stateOrProvinceCode = data.address.stateOrProvinceCode.trim();
      }

      this.fedexWizard.loading = true;
      try {
        const { data: res } = await axios.post('/popup-data/fedex-csp-validate-address', data);
        if (res.output) {
          this.fedexWizard.accountAuthToken = res.output.accountAuthToken;
          this.fedexWizard.authOptions = res.output.authenticationOptions;
          this.fedexWizard.step = 2;
          this.$store.commit(types.SHOW_NOTIFICATION_BAR, { type: 'success', message: this.$t('address_validated_success') });
        }
      } catch (e) {
        this.$store.commit(types.SHOW_NOTIFICATION_BAR, { type: 'error', message: e.response?.data?.message || 'Error validating address' });
      } finally {
        this.fedexWizard.loading = false;
      }
    },
    async validateFedExInvoice() {
      this.fedexWizard.errors = {};
      const requiredFields = ['number', 'date', 'amount', 'currency'];
      let hasErrors = false;
      requiredFields.forEach(key => {
        const value = this.fedexWizard.invoice[key];
        if (!value || (typeof value === 'string' && value.trim() === '')) {
          this.$set(this.fedexWizard.errors, `invoice.${key}`, 'Field is required');
          hasErrors = true;
        }
      });

      if (hasErrors) {
        this.$store.commit(types.SHOW_NOTIFICATION_BAR, { type: 'error', message: 'Please fill in all required fields' });
        return;
      }

      const invoiceData = {
        number: this.fedexWizard.invoice.number.toString().trim(),
        date: this.fedexWizard.invoice.date.trim(),
        amount: parseFloat(this.fedexWizard.invoice.amount),
        currency: this.fedexWizard.invoice.currency.trim()
      };

      this.fedexWizard.loading = true;
      try {
        const { data: res } = await axios.post('/popup-data/fedex-csp-validate-invoice', {
          accountAuthToken: this.fedexWizard.accountAuthToken,
          invoiceDetail: invoiceData,
          locale: 'en_US'
        });

        if (res.output && res.output.childKey) {
          this.saveFedExCspKeys(res.output.childKey, res.output.childSecret);
        }
      } catch (e) {
        this.$store.commit(types.SHOW_NOTIFICATION_BAR, { type: 'error', message: e.response?.data?.message || 'Error validating invoice' });
      } finally {
        this.fedexWizard.loading = false;
      }
    },
    saveFedExCspKeys(key, secret) {
      this.$set(this.loginParameters, 'ChildKey', key);
      this.$set(this.loginParameters, 'ChildSecret', secret);
      this.$set(this.loginParameters, 'ShippingAccount', this.fedexWizard.data.accountNumber.value.trim());
      this.fedexWizard.step = 3;
      this.$store.commit(types.SHOW_NOTIFICATION_BAR, { type: 'success', message: this.$t('fedex_account_registered_successfully') });
    },
  },
};
</script>
